import { TourElement, TourStep } from './tour.typings'

/*
 * Guided tours, one list per route key (see useRouteTourSteps). A list describes every state a page
 * can be in; resolveTourSteps keeps the steps whose targets are on screen when the tour starts.
 *
 * Copy is plain HTML rendered inside the tour tooltip: <h3> for the title, <p>/<ul> for the body,
 * <strong> for a control's name and <span class="tour-accent"> for a shortcut or a highlight.
 */

type Placement = TourElement['textPosition']
type StepOptions = Partial<Omit<TourElement, 'selector' | 'text' | 'textPosition'>> & {
    /** Extra elements to spotlight with the same tooltip. */
    also?: TourElement[]
    /** A selector that must be on the page for the step to run. */
    requires?: string
}

const step = (id: string, selector: string, placement: Placement, title: string, body: string, options: StepOptions = {}): TourStep => {
    const { also = [], requires, ...element } = options
    return {
        id,
        requires,
        elements: [{ selector, textPosition: placement, tooltipAlign: 'center', text: `<h3>${title}</h3>${body}`, ...element }, ...also]
    }
}

/** An extra spotlight in a step: no tooltip of its own. */
const also = (selector: string, options: Partial<TourElement> = {}): TourElement => ({ selector, textPosition: 'bottom', text: '', ...options })

const key = (label: string) => `<span class="tour-accent">${label}</span>`

/** Opens the Trade tab of the right sidebar. The button is only matched while the tab is closed, so the click never toggles it shut. */
const openTradeTab: TourElement['onEnter'] = {
    type: 'click',
    selector: '#side-bar-right-options [data-widget="TRD"][data-event-action="Opened"]',
    value: {}
}

const chart: TourStep[] = [
    step('chart-nav', '#side-bar-left-buttons', 'right-start', 'Every tool on one rail',
        `<p>Chart, Option Chain, Strategy Builder, OI Analysis, Screener, Heatmap, Scalper, Simulator, FII/DII and Reports.</p>
<p>Hover the rail to see the names.</p>`),
    step('chart-symbol', '#stock-bar-symbol-search', 'bottom-start', 'Pick a symbol',
        `<p>Search stocks, indices, futures and options. ${key('⌘/Ctrl + K')} opens quick search from anywhere.</p>`),
    step('chart-views', '#security-view-menu-button', 'bottom-start', 'Same symbol, other views',
        `<p>Jump to the Option Chain, Strategy Builder, OI Analysis, Scalper, Financials, Seasonals or Technicals for the symbol on screen.</p>`),
    step('chart-interval', '#stockbar-timebucket-menu-button-group', 'bottom', 'Time interval',
        `<p>From 1-second candles to monthly. Star the intervals you use most to keep them on the bar.</p>`),
    step('chart-type', '#chart-type-menu-button', 'bottom', 'Chart types',
        `<p>Candles, Heikin Ashi, hollow candles, bars, line, area, baseline, Renko, Kagi, Point &amp; Figure, Line Break and more.</p>`),
    step('chart-indicators', '#indicators-menu-button', 'bottom', 'Indicators',
        `<p>Add studies to any pane. Press ${key('/')} to search them by name.</p>`),
    step('chart-alerts', '#add-alert-menu-button', 'bottom', 'Alerts',
        `<p>Set ${key('unlimited')} price, indicator and drawing alerts straight from the chart, and never miss a move.</p>`),
    step('chart-replay', '#chart-replay-button', 'bottom', 'Chart replay',
        `<p>Step through past sessions bar by bar to test a setup before risking capital.</p>
<p>Past-expiry F&amp;O contracts can be charted too.</p>`),
    step('chart-multipane', '#multipane-menu-open-button', 'bottom-end', 'Multi-pane layouts',
        `<p>Split the screen into several panes and mix candle charts with option chains, multi-strike and OI charts.</p>`),
    step('chart-layouts', '#save-menu-open-button', 'bottom-end', 'Saved layouts',
        `<p>Name and save the layout you built, then switch between layouts in a click. ${key('Ctrl + P')} manages them.</p>`),
    step('chart-pane-tools', '.float-chart-settings-button', 'top', 'Pane tools',
        `<p>Hover the bottom of any pane for its tools: split it, change the widget, open it as a full page or reset the view.</p>`,
        { onEnter: { type: 'style', selector: '.float-chart-settings-button', value: { opacity: 1 } } }),
    step('chart-footer', '#chart-durations', 'top-start', 'Ranges and drawing tools',
        `<p>Jump to 1 day, 1 month, 1 year or more, or pick an exact date with <strong>Go to..</strong></p>
<p>On the right: trend lines, shapes, Fibonacci, forecasts, patterns and text.</p>`,
        { also: [also('#chart-drawing-toolbar')] }),
    step('chart-oi', '#oi-indicator-menu-button', 'top', 'OI on the chart',
        `<p>Plot open-interest studies over the candles: ${key('OI Profile')}, ${key('OI Outlook')} and the OI heatmap.</p>`),
    step('chart-widgets', '#side-bar-right-options', 'left-start', "Trader's panel",
        `<p>Overview, Watchlist, Alerts, News, Trade, Positions &amp; orders, Holdings, and Help &amp; feedback.</p>`),
    step('chart-utilities', '#side-bar-right-utilities', 'left-end', 'Quick widgets and more',
        `<p>Float the order book, orders, positions or a quick-trade ticket over any page. Notifications and the market calendar live here too.</p>`)
]

const optionChain: TourStep[] = [
    step('oc-symbol', '#stock-bar-symbol-search', 'bottom-start', 'Any F&amp;O underlying',
        `<p>Pick an index or a stock with futures &amp; options. The chain follows the symbol, and the view switch next to it opens the same symbol elsewhere.</p>`,
        { also: [also('#security-view-menu-button')] }),
    step('oc-quick-views', '#option-chain-quik-view-group', 'bottom', 'Quick views',
        `<p><strong>All</strong> shows OI, OI change, volume, LTP and bid/ask. <strong>Greeks</strong> adds delta, gamma, vega, theta, rho and IV. <strong>LTP</strong> is the compact price view.</p>`),
    step('oc-interval', '#option-chain-interval-menu-button', 'bottom', 'Change window',
        `<p>OI change and price change are measured over this interval, from 5 minutes to a full day.</p>`),
    step('oc-settings', '#option-chain-settings-button', 'bottom', 'Settings',
        `<p>Show all strikes or only calls, puts, OTM or ITM. Choose how many strikes to show, switch quantity to lots, and pick or reorder the columns.</p>`),
    step('oc-layout', '#option-chain-add-to-layout-button', 'bottom-end', 'Add to a layout',
        `<p>Drop this chain into a pane of your chart layout to watch it beside the candles.</p>`),
    step('oc-expiries', '#option-chain-calls-panel .expiry-date-sticky', 'bottom-start', 'Expiries',
        `<p>Each expiry is a row: click it to open or close it. <strong>W</strong> marks a weekly expiry.</p>
<p>${key('PCR')} and ${key('IVx')} are shown for every expiry on the right.</p>`,
        { also: [also('#option-chain-puts-panel .pcr-ivx-sticky')] }),
    step('oc-strikes', '#option-chain-strike-column', 'right', 'Strikes',
        `<p>The ATM strike is outlined. Click any strike for its PCR, max pain and strike charts.</p>
<p>The toggle at the top switches between mirror scroll and simple scroll for the two sides.</p>`),
    step('oc-sides', '#option-chain-calls-panel', 'right', 'Calls and puts',
        `<p>Calls on the left, puts on the right, with OI bars, outlook and live prices.</p>
<p>Click a row to stage a ${key('buy')}, click again for a ${key('sell')}, and a third time to clear it.</p>`,
        { also: [also('#option-chain-puts-panel')] }),
    step('oc-trade', '#side-bar-right-options [data-widget="TRD"]', 'left', 'Trade panel',
        `<p>Staged legs land here as a basket: set expiry, strikes and quantity, check margin and the payoff, then place all orders together.</p>`)
]

const oiAnalysis: TourStep[] = [
    step('oi-screens', '#oi-screen-options', 'bottom', 'Four ways to read OI',
        `<p><strong>Overview</strong> for the key stats, <strong>OI Change</strong> for build-up and unwinding, <strong>Open Interest</strong> for the full profile, and <strong>Multistrike</strong> to chart strikes side by side.</p>`),
    // Overview
    step('oi-overview-stats', '#oi-overview-stats', 'bottom-start', 'At a glance',
        `<p>PCR, the max call and max put OI strikes, and the net OI change for the selected expiry.</p>`),
    // Open interest
    step('oi-date', '#open-interest-date-picker', 'bottom-start', 'Pick a session',
        `<p>Open interest for any past trading day.</p>`),
    step('oi-expiries', '#open-interest-expiry-menu-button', 'bottom-start', 'Expiries',
        `<p>Choose one or more expiries to include.</p>`),
    step('oi-strikes', '#open-interest-strikes-toggle', 'bottom', 'Strikes around ATM',
        `<p>Show 10, 15, 25 or all strikes.</p>`),
    step('oi-changes', '#open-interest-show-changes', 'bottom-end', 'Show changes',
        `<p>Overlay the change in OI, and refresh it as the session moves.</p>`,
        { also: [also('#open-interest-refresh-button')] }),
    // OI change
    step('oic-range', '#oi-change-range-toggle', 'bottom-start', 'Intraday or a custom range',
        `<p>Change in OI through the session, or between any two dates.</p>`),
    step('oic-date', '#oi-change-date-picker', 'bottom-start', 'Session',
        `<p>The trading day whose OI change you are looking at.</p>`),
    step('oic-expiries', '#oi-change-expiry-menu-button', 'bottom-start', 'Expiries',
        `<p>Choose one or more expiries to include.</p>`),
    step('oic-strikes', '#oi-change-strikes-toggle', 'bottom', 'Strikes around ATM',
        `<p>Show 10, 15, 25 or all strikes.</p>`),
    step('oic-show-oi', '#oi-change-show-oi', 'bottom-end', 'Show OI and replay',
        `<p>Overlay total OI on the change bars, or replay how the change built up through the session.</p>`,
        { also: [also('#oi-change-replay-button')] }),
    // Multistrike
    step('ms-expiry', '#multistrike-expiry-menu-button', 'bottom-start', 'Expiry',
        `<p>The expiry whose strikes are charted.</p>`),
    step('ms-strikes', '#multistrike-strike-menu-button', 'bottom-start', 'Strikes',
        `<p>Pick the strikes to chart together.</p>`),
    step('ms-interval', '#multstrike-header-time-bucket-group', 'bottom', 'Interval',
        `<p>The candle size for every strike on the chart.</p>`),
    step('ms-plot', '#mulstrike-price-options', 'bottom', 'What to plot',
        `<p>Premium, IV, OI, OI change, straddle or OI difference for every selected strike, with a click.</p>`),
    step('ms-settings', '#multistrike-header-settings-button', 'bottom-end', 'Settings',
        `<p>Fine-tune expiries, strikes and how the chart is drawn.</p>`)
]

const screener: TourStep[] = [
    step('scr-screens', '#screen-layout-menu-button', 'bottom-start', 'Your screens',
        `<p>Switch between saved screens, community screens and favourites. Rename, export or email a screen from here.</p>`),
    step('scr-filters', '#screener-filters-bar', 'bottom-start', 'Filters',
        `<p>The first chip sets the universe to scan. Add conditions on price action, volume, OI, fundamentals, dividends or results; results update as you go.</p>`,
        { also: [also('#add-filter-button')] }),
    step('scr-views', '#screener-options-bar #screener-view-type', 'bottom-start', 'Table, charts or heatmap',
        `<p>See results as a table, as a wall of candle charts, or as a heatmap.</p>`),
    step('scr-columns', '#screener-column-sets', 'bottom', 'Column sets',
        `<p>Swap the columns for a ready-made set: Overview, F&amp;O, Trend-Following, Dividends, Performance, Premarket, results and more.</p>`),
    step('scr-refresh', '#screener-refresh-button', 'bottom-end', 'Refresh and settings',
        `<p>Re-run the screen now, or set it to refresh on its own.</p>`,
        { also: [also('#screener-settings-button')] }),
    step('scr-subscribe', '#screener-subscribe-button', 'bottom-end', 'Subscribe',
        `<p>Get the results of this screen by email, at day end or through the session.</p>`),
    step('scr-save', '#screener-save-button', 'bottom-start', 'Save changes',
        `<p>The screen has unsaved changes. Save them, or save a copy under a new name.</p>`)
]

const screenerExplore: TourStep[] = [
    step('explore-create', '#create-screen-button', 'bottom-start', 'Start a screen',
        `<p>Build a screen from scratch with your own filters and columns.</p>`),
    step('explore-community', '.screen-tile', 'bottom-start', 'Community screens',
        `<p>Ready-made screens from the community and the Tick2Trade team. Open one, star it, or save a copy to tweak.</p>`)
]

const fiiDii: TourStep[] = [
    step('fii-tabs', '#fii-dii-tabs', 'bottom-start', 'Four views',
        `<p>A summary of positioning, Futures &amp; Options positions by participant, Cash Market flows, and FII History.</p>`),
    // Summary
    step('fii-date', '#fii-dii-summary-date-nav', 'bottom-end', 'Any session',
        `<p>Step back through past sessions. Data is published after market close.</p>`),
    step('fii-filters', '#fii-dii-summary-filters', 'right-start', 'Filters',
        `<p>Choose the participants (FII, Pro, Client, DII) and segments (index and stock futures, index options, stocks) to show.</p>`),
    step('fii-table', '#fii-dii-summary-table', 'left-start', 'Reading the summary',
        `<p>Each row shows net OI and its change, with a bearish-to-bullish gauge of how the participant is positioned.</p>`),
    // Futures and options
    step('fii-fo-participant', '#fii-dii-fo-participants', 'bottom-start', 'Participant',
        `<p>Switch between FII, DII, Pro and Client positions.</p>`),
    step('fii-fo-month', '#fii-dii-fo-month', 'bottom-end', 'Month',
        `<p>Pick the month to track.</p>`),
    // Cash market
    step('fii-cash-view', '#fii-dii-cash-view-toggle', 'bottom-start', 'Daily or monthly',
        `<p>Cash-market buy, sell and net flows by day or by month.</p>`),
    step('fii-cash-month', '#fii-dii-cash-month', 'bottom-end', 'Month',
        `<p>Pick the month to track.</p>`),
    // FII history
    step('fii-history-month', '#fii-dii-history-month', 'bottom-end', 'Month',
        `<p>FII activity across the month.</p>`)
]

const strategyBuilder: TourStep[] = [
    step('sb-templates', '#select-strategy-button', 'bottom-start', 'Strategy templates',
        `<p>Bullish, bearish and neutral templates, plus the ones you save yourself.</p>`),
    // Template gallery (no strategy yet)
    step('sb-tile', '[data-event-strategy-name="Short Straddle"]', 'bottom', 'Pick a template',
        `<p>One click builds the legs on the current symbol. Every template can be tuned afterwards.</p>`),
    step('sb-new', '#build-strategy-button', 'bottom-end', 'Build your own',
        `<p>Start empty and add legs from the option chain.</p>`),
    // Builder (a strategy is open)
    step('sb-tabs', '#strategy-builder-tabs', 'bottom', 'Payoff, chain and chart',
        `<p>The payoff graph, the option chain to add legs, and the ${key('Strategy Chart')} for technicals on the combined premium.</p>`),
    step('sb-legs', '#trade-panel-legs', 'left-start', 'Legs',
        `<p>Buy or sell, expiry, strike, type and quantity for each leg. Untick a leg to leave it out, or remove it.</p>`,
        { lazy: true, delay: 400, onEnter: openTradeTab, requires: '#strategy-builder-tabs' }),
    step('sb-parameters', '#strategy-parameters', 'left', 'Adjust the whole strategy',
        `<p>Shift the expiry, move every strike up or down, scale the quantity, or swap calls and puts, without going back to the chain.</p>`,
        { lazy: true, delay: 400, onEnter: openTradeTab, requires: '#strategy-builder-tabs' }),
    step('sb-iv', '#iv-multiplier-input', 'top-start', 'IV vs P&amp;L',
        `<p>Scale implied volatility from 0x to 2x and watch the payoff move.</p>`),
    step('sb-spot', '#spot-price-input', 'top', 'Spot price',
        `<p>Set a target spot to read the expected P&amp;L there.</p>`),
    step('sb-target-date', '#target-day-container', 'top', 'Target date',
        `<p>P&amp;L on any date before expiry, time decay included.</p>`),
    step('sb-place', '#trade-panel-place-orders-button', 'top', 'Place orders',
        `<p>Send every leg as one basket to your connected broker, or try it in practice mode first.</p>`,
        { lazy: true, delay: 400, onEnter: openTradeTab, requires: '#strategy-builder-tabs' })
]

const scalper: TourStep[] = [
    step('scalper-symbol', '#stock-bar-symbol-search', 'bottom-start', 'Pick the underlying',
        `<p>The scalper follows one F&amp;O underlying. It finds the nearest expiry and the at-the-money strike, and fills the panes with the spot, call, put or multi-strike charts of the layout you choose.</p>`),
    step('scalper-interval', '#stockbar-timebucket-menu-button-group', 'bottom', 'Fast intervals',
        `<p>Trade off 1-second, 30-second or 1-minute candles. Starred intervals stay on the bar.</p>`),
    step('scalper-trade-bar', '.chart-bid-ask-controls', 'bottom-start', 'Buy and sell from the chart',
        `<p>Every pane shows the best bid on the ${key('sell')} button and the best ask on the ${key('buy')} button, with the quantity in lots between them.</p>
<p>With quick trade on, a click sends the order straight to your broker or practice account, no confirmation. With it off, a click opens the order ticket first.</p>`),
    step('scalper-quick-trade', '#scalper-quick-trade-toggle', 'bottom', 'Quick trade',
        `<p>Switch one-click orders on or off. The chart buttons show a ${key('⚡')} while a click will place an order.</p>`),
    step('scalper-settings', '#scalper-layout-settings-open-button', 'bottom-end', 'Order settings',
        `<p>How every quick order goes out: the quantity multiplier in lots, Intraday or Overnight, and market or limit. Limit orders are priced from the top of the book.</p>`),
    step('scalper-layouts', '#scalper-layout-menu-open-button', 'bottom-end', 'Scalper layouts',
        `<p>Choose the panes and how they are arranged: call &amp; put, spot + call/put, multi-strike + call &amp; put, and more.</p>`),
    step('scalper-positions', '#side-bar-right-options [data-widget="POS"]', 'left', 'Positions and orders',
        `<p>Open positions and orders sit in the sidebar, with square-off a click away.</p>`)
]

export const pathStepsMap: { [key: string]: TourStep[] } = {
    chart,
    'option-chain': optionChain,
    'oi-analysis': oiAnalysis,
    screener,
    'screener-explore': screenerExplore,
    'fii-dii': fiiDii,
    'strategy-builder': strategyBuilder,
    scalper
}

export default pathStepsMap
